import { Button, Text } from '@tremor/react';
import type { RemoteSyncStatus } from '../api/admin';
import { t } from '../lib/i18n';

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return t.admin.remote.justNow;
  if (min < 60) return t.admin.remote.minutesAgo.replace('{n}', String(min));
  return t.admin.remote.hoursAgo.replace('{n}', String(Math.round(min / 60)));
}

/**
 * Status strip for the remote-access user sync (spec 2026-09-05). Renders
 * nothing when the install has no licensing service to sync with.
 */
export default function RemoteSyncStrip({ status, onSyncNow }: { status: RemoteSyncStatus | null; onSyncNow: () => void }) {
  if (!status || !status.enabled) return null;
  const errors = t.admin.remote.errors as Record<string, string>;
  let tone = 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
  let text: string;
  if (status.pending) {
    tone = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
    text = t.admin.remote.syncing;
  } else if (status.error_code) {
    tone = 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
    text = errors[status.error_code] ?? errors.generic;
  } else if (status.last_ok && !status.last_ok.startsWith('0001')) {
    text = t.admin.remote.synced.replace('{when}', ago(status.last_ok)).replace('{n}', String(status.accepted));
  } else {
    text = t.admin.remote.never;
  }
  return (
    <div className={`rounded-tremor-default px-3 py-2 flex items-center justify-between gap-3 ${tone}`}>
      <Text className="text-inherit"><strong>{t.admin.remote.title}:</strong> {text}</Text>
      <Button size="xs" variant="secondary" disabled={status.pending} onClick={onSyncNow}>{t.admin.remote.syncNow}</Button>
    </div>
  );
}
